import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridColSpanFn, GridRenderCellParams } from "@mui/x-data-grid";
import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { type MouseEvent, memo, type ReactNode } from "react";
import { ALPHA, FONT_SIZE, ICON_SIZE, INK, NEUTRAL, STAFF_PICKER, STATUS } from "../../theme/tokens";
import type { NurseData, NurseRowBadge } from "../../utils/models";
import type { MultiSortOption } from "../../utils/multiColumnSorting";
import { DenyNurseButton, RestoreNurseButton } from "../approvalDialogs/RequestsApproval/DenyNurseButton";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { NurseNameDisplay } from "../NurseNameDisplay";
import MetaChip from "../UIToolkit/Badges/MetaChip";
import { OvertimeCell } from "../UIToolkit/Cells/OvertimeCell/OvertimeCell";
import { shouldShowOtColumn } from "../UIToolkit/Cells/OvertimeCell/otVisibility";
import { formatRequestedAt, RequestedAtCell } from "../UIToolkit/Cells/RequestedAtCell/RequestedAtCell";
import { SeniorityCell } from "../UIToolkit/Cells/SeniorityCell/SeniorityCell";
import { RoundedCheckboxBox, RoundedCheckboxState } from "../UIToolkit/Clickable/RoundedCheckboxBox";
import { RoundedRadioBox } from "../UIToolkit/Clickable/RoundedRadioBox";
import type { SystemGridColDef } from "../UIToolkit/Tables/SystemDataGrid/types";
import { NURSE_TYPE_FIELD, REQUESTED_AT_FIELD, SENIORITY_FIELD } from "./columnVisibility";
import { type NursesMatrixRowId, type NursesMatrixSelectionMode, SELECT_COLUMN_FIELD } from "./consts";
import { buildDayColumns } from "./dayColumns";

/**
 * Class on the (always-rendered) selection checkmark in the name cell. It is `display: none` by default;
 * NursesMatrixGrid's `sx` reveals it only on the `.selected-row` row. Keeping the icon out of the column
 * builder (driven by CSS instead of a `selectedNurseId` prop) means a row click no longer rebuilds every
 * column — only the row's class flips.
 */
export const SELECTED_CHECK_CLASS = "nurses-matrix-selected-check";

const EMPTY_CELL = "--";

// ── Cell components (memoized so a row click / re-sort doesn't re-render every cell) ──

/**
 * A blocked row's name: the role pill and name recede together and the name is struck through — still
 * readable (the reason beside it refers to this person) but plainly not an option.
 */
const blockedNameSx = {
	display: "flex",
	alignItems: "center",
	flex: "1 1 auto",
	minWidth: 0,
	opacity: STAFF_PICKER.BLOCKED_NAME_OPACITY,
	"& .MuiTypography-root": { textDecoration: "line-through", textDecorationColor: alpha(INK.MUTED, ALPHA.STRUCK) },
} as const;

/**
 * Flex-shrink weight of the name's trailing chips against the name's own 1: so lopsided that a narrow name cell
 * takes its missing room out of the chips first, and the name only ellipsizes once they are at their floor.
 */
const NAME_BADGES_SHRINK_FIRST = 1000;
/** Gap (theme spacing units) between the name's trailing chips. */
const NAME_BADGES_GAP = 0.5;

/**
 * Quiet trailing chips (PTO, OT, long day, fully booked) — they annotate the name, never compete with it. In a
 * narrow name column they give way first: each chip shrinks to its first letter + "…" (its tooltip keeps the
 * full text) rather than pushing the name out of the cell — never below that, so every chip stays visible and
 * hoverable. The row's floor is spelled out because a chip's own min-content is its whole unwrapped label.
 */
const NameBadges = memo(function NameBadges({ badges, rowTestId }: { badges: NurseRowBadge[]; rowTestId?: string }) {
	return (
		<Box
			sx={(theme) => ({
				display: "flex",
				alignItems: "center",
				gap: NAME_BADGES_GAP,
				flex: `0 ${NAME_BADGES_SHRINK_FIRST} auto`,
				minWidth: `calc(${badges.length * STAFF_PICKER.NAME_BADGE_MIN_WIDTH}px + ${theme.spacing(NAME_BADGES_GAP * (badges.length - 1))})`,
				overflow: "hidden",
				ml: 1,
				"& > *": { minWidth: STAFF_PICKER.NAME_BADGE_MIN_WIDTH },
			})}
		>
			{badges.map((badge) => (
				<MetaChip
					key={badge.kind}
					label={badge.label}
					tone={badge.tone}
					// DEFAULT: a badge with no detail tooltip shows its own label on hover — a narrow name cell can
					// ellipsize the chip to "P…", so the hover is the only place its full text is still readable.
					tooltip={badge.tooltip ?? badge.label}
					dataTestId={rowTestId ? `${rowTestId}-badge-${badge.kind}` : undefined}
				/>
			))}
		</Box>
	);
});

const NurseNameCell = memo(function NurseNameCell({
	nurse,
	isPartial,
	primaryColor,
	rowTestId,
	selectionMode,
	t,
}: {
	nurse: NurseData;
	isPartial: boolean;
	primaryColor: string;
	rowTestId?: string;
	/** Set → the pick lives in the leading select column, so the legacy CheckCircle is not rendered. */
	selectionMode?: NursesMatrixSelectionMode;
	t: TFunction;
}) {
	const isBlocked = nurse.unavailableReason != null;
	// A blocked row's soft notes are moot — its reason already says why it isn't an option.
	const badges = !isBlocked && nurse.badges && nurse.badges.length > 0 ? nurse.badges : null;
	const nameDisplay = (
		<NurseNameDisplay
			nurse={nurse}
			role={nurse.role}
			charge={nurse.charge}
			trainingStatus={nurse.trainingStatus}
			maxWidth="100%"
			// The selection modes sit in a roomier picker (taller rows), so the name steps up one size.
			size={selectionMode ? "large" : "medium"}
			caption={nurse.nameCaption}
		/>
	);
	return (
		<Box sx={{ display: "flex", alignItems: "center", width: "100%", overflow: "hidden" }} data-testid={rowTestId}>
			{isBlocked ? (
				<Box sx={blockedNameSx}>{nameDisplay}</Box>
			) : badges ? (
				// Hug the name so the chips sit right after it, instead of the name filling the cell and pushing
				// them to its far edge where they read as a column of their own.
				<Box sx={{ display: "flex", alignItems: "center", flex: "0 1 auto", minWidth: 0 }}>{nameDisplay}</Box>
			) : (
				nameDisplay
			)}
			{badges && <NameBadges badges={badges} rowTestId={rowTestId} />}
			{/* Always rendered; shown by CSS only on the selected row (see SELECTED_CHECK_CLASS). */}
			{!selectionMode && (
				<CheckCircleIcon
					className={SELECTED_CHECK_CLASS}
					sx={{ display: "none", fontSize: 16, color: primaryColor, ml: 0.5 }}
					aria-label={t("nurses.matrix.selected")}
				/>
			)}
			{isPartial && <AccessTimeIcon sx={{ fontSize: 16, color: STATUS.ERROR.main }} />}
			{nurse.agencyProfileUrls?.map((url) => (
				<Tooltip key={url} title={t("nurses.matrix.view.agency.profile")}>
					<IconButton
						component="a"
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						size="small"
						onClick={(e: MouseEvent) => e.stopPropagation()}
						sx={{ ml: 0.5, p: 0.25 }}
						aria-label={t("nurses.matrix.view.agency.profile")}
					>
						<OpenInNewIcon sx={{ fontSize: 14 }} />
					</IconButton>
				</Tooltip>
			))}
		</Box>
	);
});

const PredictedCostCell = memo(function PredictedCostCell({ predictedCost, bonus }: { predictedCost: string | undefined; bonus: number | undefined }) {
	return (
		<Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS }}>{predictedCost}</Typography>
			{!!bonus && <Typography sx={{ fontSize: FONT_SIZE.EMPHASIS }}>(+${bonus})</Typography>}
		</Box>
	);
});

const PreferencesCell = memo(function PreferencesCell({ preferences }: { preferences: string }) {
	return (
		<InHouseTooltip title={preferences} arrow>
			<Typography
				sx={{
					fontSize: FONT_SIZE.SECONDARY,
					color: INK.SECONDARY,
					fontWeight: 400,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					maxWidth: "100%",
				}}
			>
				{preferences}
			</Typography>
		</InHouseTooltip>
	);
});

const DenyActionCell = memo(function DenyActionCell({
	nurseId,
	isServerDenied,
	isQueuedForDeny,
	onDeny,
	onRestore,
}: {
	nurseId: number;
	isServerDenied: boolean;
	isQueuedForDeny: boolean;
	onDeny: (nurseId: number) => void;
	onRestore?: (nurseId: number) => void;
}) {
	if (isServerDenied) return null;
	if (isQueuedForDeny) return onRestore ? <RestoreNurseButton nurseId={nurseId} onRestore={onRestore} /> : null;
	return <DenyNurseButton nurseId={nurseId} onDeny={onDeny} />;
});

/**
 * The leading picker control: a radio (single) or checkbox (multiple) whose state comes from the row's
 * `picked` flag, or — on a blocked row — a muted ✕ that names the reason on hover. The row itself is
 * the click target (the grid's row click picks), so the glyph is visual and this host carries the
 * `role`/`aria-checked` semantics.
 */
const SelectCell = memo(function SelectCell({
	mode,
	picked,
	name,
	unavailableReason,
	testId,
}: {
	mode: NursesMatrixSelectionMode;
	picked: boolean;
	name: string;
	unavailableReason: string | undefined;
	testId?: string;
}) {
	if (unavailableReason != null) {
		return (
			<InHouseTooltip title={unavailableReason} arrow>
				<Box component="span" role="img" aria-label={unavailableReason} data-testid={testId} sx={{ display: "inline-flex", color: NEUTRAL[450] }}>
					<CloseRoundedIcon sx={{ fontSize: ICON_SIZE.MICRO }} />
				</Box>
			</InHouseTooltip>
		);
	}
	return (
		<Box
			component="span"
			role={mode === "multiple" ? "checkbox" : "radio"}
			aria-checked={picked}
			aria-label={name}
			data-testid={testId}
			sx={{ display: "inline-flex" }}
		>
			{mode === "multiple" ? (
				<RoundedCheckboxBox state={picked ? RoundedCheckboxState.On : RoundedCheckboxState.Off} animated aria-hidden />
			) : (
				<RoundedRadioBox checked={picked} animated aria-hidden />
			)}
		</Box>
	);
});

/** Why a blocked row can't take the shift, laid across the metadata columns it replaces. */
const BlockedReasonCell = memo(function BlockedReasonCell({ reason, testId }: { reason: string; testId?: string }) {
	return (
		<Typography
			component="span"
			noWrap
			data-testid={testId}
			sx={{ width: "100%", textAlign: "start", fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, overflow: "hidden", textOverflow: "ellipsis" }}
		>
			{reason}
		</Typography>
	);
});

// ── Column builder ──

export interface BuildNursesMatrixColumnsConfig {
	nursesData: NurseData[];
	dateRange: DateTime[];
	highlightDateIndexes: number[];
	isAdd: boolean;
	/** True when the given nurse is a partial-shift acceptor for the active request. */
	getIsPartial: (nurseId: number) => boolean;
	hightlightOriginalShift: boolean;
	canViewWage: boolean;
	isJobManagementEnabled: boolean;
	/** Strict inhouse2+Hospital flag — dark day/night icons show only for the real hospital demo. */
	darkShiftIcons: boolean;
	/** Medical departments never surface the OT column (matches the popup + mobile cards). */
	isMedicalDepartment: boolean;
	onDenyNurse?: (nurseId: number) => void;
	onRestoreNurse?: (nurseId: number) => void;
	queuedDenyIdSet: Set<number>;
	primaryColor: string;
	t: TFunction;
	/** Grid `data-testid`; when set, each name cell gets `${dataTestId}-row-${nurseId}` for per-row targeting. */
	dataTestId?: string;
	/** Set → a leading `__select` radio/checkbox column is added and the name cell drops its CheckCircle. */
	selectionMode?: NursesMatrixSelectionMode;
	/** Day index whose cell previews the pending shift on picked rows (selection modes). */
	pendingDayIndex?: number;
}

const METADATA_CELL_ALIGN = "center" as const;

// Shared column defaults spread into every column. `display: "flex"` vertically centers cell content
// (MUI X top-aligns custom `renderCell` output by default) while still honoring each column's `align`
// for horizontal alignment.
const columnBase = { display: "flex" } as const;

/**
 * Builds the dynamic metadata columns (gated exactly as the legacy table), the N day columns, and the
 * optional trailing deny column — plus the `sortingOptions` catalog derived from the sortable columns
 * so the grid toolbar's sort popup and the clickable headers stay in sync.
 */
export function buildNursesMatrixColumns(config: BuildNursesMatrixColumnsConfig): {
	columns: SystemGridColDef<NurseData>[];
	sortingOptions: MultiSortOption<NurseData>[];
} {
	const columns: SystemGridColDef<NurseData>[] = [];
	const sortingOptions: MultiSortOption<NurseData>[] = [];
	const firstNurse: Partial<NurseData> = config.nursesData[0] ?? {};

	const addSortable = (option: MultiSortOption<NurseData>) => sortingOptions.push(option);

	buildSelectColumn(config, columns);
	const firstMetadataIndex = columns.length + 1; // the name column precedes every metadata column
	buildMetadataColumns(config, firstNurse, columns, addSortable);
	// Gated on the data so grids with no blocked rows keep their metadata columns exactly as built.
	if (config.nursesData.some((nurse) => nurse.unavailableReason != null)) applyBlockedReasonSpan(columns.slice(firstMetadataIndex), config.dataTestId);
	buildDayColumns(config, columns);
	buildDenyColumn(config, columns);

	return { columns, sortingOptions };
}

/** Row identity inside the grid — matches `NursesMatrixGrid`'s `getRowId`. */
const rowIdOf = (row: NurseData): NursesMatrixRowId => row.rowKey ?? row.id;

function buildSelectColumn(config: BuildNursesMatrixColumnsConfig, columns: SystemGridColDef<NurseData>[]): void {
	const { selectionMode, dataTestId } = config;
	if (!selectionMode) return;
	columns.push({
		...columnBase,
		field: SELECT_COLUMN_FIELD,
		excludeFromColumnPicker: true,
		disableReorder: true,
		headerName: "",
		width: STAFF_PICKER.SELECT_COLUMN_WIDTH,
		sortable: false,
		disableColumnMenu: true,
		disableExport: true,
		align: "center",
		headerAlign: "center",
		renderCell: (params: GridRenderCellParams<NurseData>) => (
			<SelectCell
				mode={selectionMode}
				picked={params.row.picked === true}
				name={params.row.name}
				unavailableReason={params.row.unavailableReason}
				testId={dataTestId ? `${dataTestId}-select-${rowIdOf(params.row)}` : undefined}
			/>
		),
	});
}

/**
 * Length of the run of visible metadata columns that STARTS at `field` — 0 when `field` sits inside a
 * run (a metadata column precedes it) or is hidden. Read from the live visible order, so hiding a column
 * from the picker re-flows the span instead of spilling the reason into a day column.
 */
function metadataRunFrom(field: string, visible: ReadonlyArray<{ field: string }>, metadataFields: ReadonlySet<string>): number {
	const start = visible.findIndex((column) => column.field === field);
	if (start < 0 || (start > 0 && metadataFields.has(visible[start - 1].field))) return 0;
	let run = 0;
	while (start + run < visible.length && metadataFields.has(visible[start + run].field)) run++;
	return run;
}

/**
 * A blocked row (`unavailableReason`) shows its reason in place of its metadata: the first visible
 * metadata column col-spans every visible metadata column after it and renders the reason; the
 * figures it covers (cost, OT, tenure…) are not an option for this shift anyway. Rows without a reason
 * are untouched — each column keeps its own span and cell.
 */
function applyBlockedReasonSpan(metadataColumns: SystemGridColDef<NurseData>[], dataTestId: string | undefined): void {
	const metadataFields = new Set(metadataColumns.map((column) => column.field));
	for (const column of metadataColumns) {
		const innerRenderCell = column.renderCell;
		const innerColSpan = column.colSpan;
		const colSpan: GridColSpanFn<NurseData> = (value, row, colDef, apiRef) => {
			if (row.unavailableReason != null) return Math.max(1, metadataRunFrom(colDef.field, apiRef.current.getVisibleColumns(), metadataFields));
			return typeof innerColSpan === "function" ? innerColSpan(value, row, colDef, apiRef) : innerColSpan;
		};
		column.colSpan = colSpan;
		column.renderCell = (params: GridRenderCellParams<NurseData>) => {
			const reason = params.row.unavailableReason;
			if (reason == null) {
				if (innerRenderCell) return innerRenderCell(params);
				// The grid's own default cell: the formatted value, else the raw one (both untyped `any` here).
				const plain: ReactNode = params.formattedValue ?? params.value ?? null;
				return plain;
			}
			if (metadataRunFrom(params.field, params.api.getVisibleColumns(), metadataFields) === 0) return null;
			return <BlockedReasonCell reason={reason} testId={dataTestId ? `${dataTestId}-reason-${rowIdOf(params.row)}` : undefined} />;
		};
	}
}

function buildMetadataColumns(
	config: BuildNursesMatrixColumnsConfig,
	firstNurse: Partial<NurseData>,
	columns: SystemGridColDef<NurseData>[],
	addSortable: (option: MultiSortOption<NurseData>) => void,
): void {
	const { nursesData, canViewWage, isAdd, isJobManagementEnabled, isMedicalDepartment, t, dataTestId } = config;

	columns.push({
		...columnBase,
		field: "name",
		excludeFromColumnPicker: true,
		headerName: t("name"),
		flex: 2,
		minWidth: 170,
		align: "left",
		headerAlign: "left",
		renderCell: (params: GridRenderCellParams<NurseData>) => (
			<NurseNameCell
				nurse={params.row}
				isPartial={config.getIsPartial(params.row.id)}
				primaryColor={config.primaryColor}
				rowTestId={dataTestId ? `${dataTestId}-row-${params.row.id}` : undefined}
				selectionMode={config.selectionMode}
				t={t}
			/>
		),
	});
	addSortable({ sortColumns: ["name"], label: t("name") });

	if (firstNurse.weekends != null) {
		columns.push({
			...columnBase,
			field: "weekends",
			headerName: t("weekends"),
			flex: 1,
			minWidth: 90,
			sortable: false,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
		});
	}

	if (firstNurse.changes != null) {
		columns.push({
			...columnBase,
			field: "changes",
			headerName: t("changes"),
			flex: 1,
			minWidth: 90,
			sortable: false,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
		});
	}

	if (firstNurse.predictedCost != null && canViewWage) {
		columns.push({
			...columnBase,
			// Sort by the numeric backing value, not the formatted `$…` string. `field` is the numeric key so
			// the auto-injected sortable header and the popup option resolve to the same numeric sort entry.
			field: "predictedCostValue",
			headerName: t("predicted.cost"),
			flex: 1.3,
			minWidth: 110,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<NurseData>) => <PredictedCostCell predictedCost={params.row.predictedCost} bonus={params.row.bonus} />,
		});
		// `sortNullUndefinedLast` keeps nurses without a prediction at the end in BOTH directions (via
		// useMultiSort's grid-wide nulls-last), instead of flipping to the top on descending.
		addSortable({ sortColumns: ["predictedCostValue"], label: t("predicted.cost"), sortNullUndefinedLast: true });
	}

	if (shouldShowOtColumn({ hasOvertimeData: firstNurse.overtimeHours != null, isMedicalDepartment })) {
		const showWeeklyOt = firstNurse.weeklyOvertimeHoursBeforeChange != null;
		const otLabel = showWeeklyOt ? t("nurses.matrix.ot") : isAdd ? t("nurses.matrix.additional.ot") : t("nurses.matrix.removed.ot");
		columns.push({
			...columnBase,
			field: "overtimeHours",
			headerName: otLabel,
			flex: 1.3,
			minWidth: 100,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<NurseData>) => (
				<OvertimeCell
					overtimeHours={params.row.overtimeHours ?? 0}
					weeklyOvertimeHoursBeforeChange={params.row.weeklyOvertimeHoursBeforeChange}
					showWeeklyOt={showWeeklyOt}
					isAdd={isAdd}
					t={t}
				/>
			),
		});
		addSortable({ sortColumns: ["overtimeHours"], label: otLabel });
	}

	columns.push({
		...columnBase,
		field: SENIORITY_FIELD,
		headerName: t("nurses.matrix.seniority"),
		flex: 1.3,
		minWidth: 100,
		align: METADATA_CELL_ALIGN,
		headerAlign: "center",
		renderCell: (params: GridRenderCellParams<NurseData>) => <SeniorityCell nurseStartDate={params.row.nurseStartDate} t={t} />,
	});
	addSortable({ sortColumns: [SENIORITY_FIELD], label: t("nurses.matrix.seniority") });

	if (firstNurse.nurseType != null) {
		columns.push({
			...columnBase,
			field: NURSE_TYPE_FIELD,
			headerName: t("nurses.matrix.nurse.type"),
			flex: 1.3,
			minWidth: 100,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
		});
		addSortable({ sortColumns: [NURSE_TYPE_FIELD], label: t("nurses.matrix.nurse.type") });
	}

	if (isJobManagementEnabled && nursesData.some((n) => n.jobName != null)) {
		columns.push({
			...columnBase,
			field: "jobName",
			headerName: t("job"),
			flex: 1.3,
			minWidth: 100,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<NurseData>) => <>{params.row.jobName ?? EMPTY_CELL}</>,
		});
		addSortable({ sortColumns: ["jobName"], label: t("job") });
	}

	if (firstNurse.preferences != null) {
		columns.push({
			...columnBase,
			field: "preferences",
			headerName: t("work.preferences"),
			flex: 1.3,
			minWidth: 110,
			sortable: false,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<NurseData>) => <PreferencesCell preferences={params.row.preferences || ""} />,
		});
	}

	if (firstNurse.lastWeekCalloffs) {
		columns.push({
			...columnBase,
			field: "lastWeekCalloffs",
			headerName: t("nurses.matrix.shift.removals"),
			flex: 1.4,
			minWidth: 110,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<NurseData>) => <>{renderShiftRemovals(params.row.lastWeekCalloffs, t)}</>,
		});
		addSortable({ sortColumns: ["lastWeekCalloffs"], label: t("nurses.matrix.shift.removals") });
	}

	if (nursesData.some((n) => n.shiftTimes)) {
		columns.push({
			...columnBase,
			// Primary sort key = shiftStartTime (with shiftEndTime as tie-break) so the header + popup agree.
			field: "shiftStartTime",
			headerName: t("shift.times"),
			flex: 1.7,
			minWidth: 130,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<NurseData>) => (
				<>{params.row.shiftTimes ? `${params.row.shiftTimes.start.toFormat("h:mm a")} - ${params.row.shiftTimes.end.toFormat("h:mm a")}` : ""}</>
			),
		});
		addSortable({ sortColumns: ["shiftStartTime", "shiftEndTime"], label: t("shift.times") });
	}

	// Data-gated so the column stays out of the other NursesMatrixGrid surfaces. Checked across all nurses,
	// not just the first, because a declined nurse carries no timestamp and can sit at index 0.
	if (nursesData.some((n) => n.requestedAt != null)) {
		columns.push({
			...columnBase,
			field: REQUESTED_AT_FIELD,
			headerName: t("nurses.matrix.requested.at"),
			flex: 1.7,
			// 170: a full DATETIME_MED value ellipsizes below this once flex columns hit their floor.
			minWidth: 170,
			align: METADATA_CELL_ALIGN,
			headerAlign: "center",
			// valueFormatter feeds clipboard copy / export; renderCell owns the display and tooltip.
			valueFormatter: (value: DateTime | null | undefined) => formatRequestedAt(value),
			renderCell: (params: GridRenderCellParams<NurseData>) => (
				<RequestedAtCell requestedAt={params.row.requestedAt} cellTestId={dataTestId ? `${dataTestId}-requested-at-${params.row.id}` : undefined} />
			),
		});
		// Keeps declined nurses last in both directions. Note useMultiSort applies this flag grid-wide.
		addSortable({ sortColumns: [REQUESTED_AT_FIELD], label: t("nurses.matrix.requested.at"), sortNullUndefinedLast: true });
	}
}

function buildDenyColumn(config: BuildNursesMatrixColumnsConfig, columns: SystemGridColDef<NurseData>[]): void {
	const { onDenyNurse, onRestoreNurse, queuedDenyIdSet } = config;
	if (!onDenyNurse) return;
	columns.push({
		...columnBase,
		field: "__deny",
		excludeFromColumnPicker: true,
		headerName: "",
		width: 52,
		sortable: false,
		disableColumnMenu: true,
		disableExport: true,
		align: "center",
		headerAlign: "center",
		renderCell: (params: GridRenderCellParams<NurseData>) => (
			<DenyActionCell
				nurseId={params.row.id}
				isServerDenied={params.row.isDenied === true}
				isQueuedForDeny={queuedDenyIdSet.has(params.row.id)}
				onDeny={onDenyNurse}
				onRestore={onRestoreNurse}
			/>
		),
	});
}

function renderShiftRemovals(lastWeekCalloffs: NurseData["lastWeekCalloffs"], t: TFunction): ReactNode {
	if (!lastWeekCalloffs || lastWeekCalloffs.count === 0) return 0;
	const { count, lastCalloffTime } = lastWeekCalloffs;
	return (
		<Tooltip title={t("nurses.matrix.last.calloff", { date: lastCalloffTime.toFormat("MMM D") })}>
			<span>{count}</span>
		</Tooltip>
	);
}
